import { useIsUserHcpVerificationEnabled } from '@/features/flags/hcp-verification-flags';
import { useAuthToken } from '@/hooks/useAuthToken';
import { useQueryUser } from '@heidi/workspace/data';
import { skipToken, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  type HcpVerificationStatusResponse,
  getHcpSpecialtyRequirement,
  getHcpVerificationStatus,
} from './hcp-verification-api';
import { GET_HCP_VERIFICATIONS_QUERY_KEY } from './hcp-verification-query-key';

export { GET_HCP_VERIFICATIONS_QUERY_KEY };

/**
 * `GET /user` stores the country as its display name (the value the backend's
 * `Country.UNITED_STATES` enum resolves to), not an ISO code. The Kinde region
 * code (`US`) is a different source and must not be used here.
 */
export const HCP_VERIFICATION_SUPPORTED_COUNTRY = 'United States';

const selectStatus = (response: HcpVerificationStatusResponse) => response.status;

const isClientError = (error: unknown): boolean =>
  isAxiosError(error) &&
  error.response !== undefined &&
  error.response.status >= 400 &&
  error.response.status < 500;

/**
 * The caller's clinician verification status, read only when the client-side
 * gate passes: release flag ON, US account, resolved user, and a session
 * token. Off that gate the query is idle (`isEnabled: false`) and no request
 * is issued.
 *
 * The token check is load-bearing. `getUser` and the release flag both survive
 * an expired session, so country and flag can still pass while signed out. A
 * request with no token omits `Authorization` and is sent to the AU API host.
 *
 * `data` is the status the backend ranked across every registry, `null` when
 * the caller holds no record, and `undefined` until the first read settles.
 *
 * The backend gate answers 403 for a caller outside its own scope; that is a
 * deterministic "not eligible" (`isGated: true`), never retried.
 *
 * `observeOnly` reads whatever another observer has fetched and never issues
 * the request itself. The backend writes the day's reminder inmail on the first
 * read, so a caller that only needs the value must not become a trigger for it.
 */
export function useQueryHcpVerifications({
  observeOnly = false,
}: { observeOnly?: boolean } = {}) {
  const isFlagEnabled = useIsUserHcpVerificationEnabled();
  const token = useAuthToken();
  const { data: gate } = useQueryUser((user) => ({
    userRefId: user?.user_ref_id ?? null,
    isSupportedCountry: user?.country === HCP_VERIFICATION_SUPPORTED_COUNTRY,
  }));
  const userRefId = gate?.userRefId ?? null;
  const isEnabled =
    isFlagEnabled && gate?.isSupportedCountry === true && userRefId !== null && !!token;

  const query = useQuery({
    queryKey: [GET_HCP_VERIFICATIONS_QUERY_KEY, userRefId],
    queryFn: isEnabled
      ? async () => {
          const { data } = await getHcpVerificationStatus();
          return data;
        }
      : skipToken,
    // Not `skipToken`: an observer's options overwrite the shared query's, so a
    // skipped queryFn here would break the fetching observer's refetches.
    enabled: !observeOnly,
    select: selectStatus,
    staleTime: 5 * 60 * 1000,
    // 4xx (403 gate, 401, 429 rate limit) is deterministic for this call; only
    // transport / 5xx failures get the app-wide single retry.
    retry: (failureCount, error) => !isClientError(error) && failureCount < 1,
  });

  const isGated =
    query.isError && isAxiosError(query.error) && query.error.response?.status === 403;

  return {
    data: query.data,
    isPending: query.isPending,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    dataUpdatedAt: query.dataUpdatedAt,
    refetch: query.refetch,
    isEnabled,
    isGated,
  };
}

/**
 * Whether the caller's saved specialty needs the registry, read when the
 * verification dialog opens; see `getHcpSpecialtyRequirement`. Never served
 * from cache (`gcTime: 0`): a specialty changed since the last read would
 * leave a cached answer wrong.
 */
export function useQueryHcpSpecialtyRequirement() {
  return useQuery({
    queryKey: [GET_HCP_VERIFICATIONS_QUERY_KEY, 'specialtyRequirement'],
    queryFn: getHcpSpecialtyRequirement,
    gcTime: 0,
    retry: (failureCount, error) => !isClientError(error) && failureCount < 1,
  });
}
